import React, { useEffect, useState } from "react";
import Container from "common/components/Container/Container";
import i18n from "internationalization/i18n";
import { useDispatch, useSelector } from "react-redux";
import { rootReducerType } from "../../redux/combineReducers";
import useWindowDimensions, { counterCommonItemsArrays, decodeJWT, getDataToDisplay } from "../../common/functions";
import { Button, Checkbox, Modal } from 'antd';
import { emailToUsername, getUpperCase } from "../../common/utils";
import SelectFilter from "../../common/components/Select/Select";
import PricingTable from "components/PricingTable/PricingTable";
import { useGetStationsQuery } from "../../services/stationsService";
import { UserItem } from "services/utils/interfaces";
import Notification from "common/components/NotificationMessage/Notification";
import {
	pricingApi, useGetPricingPlansQuery, useGetStationPricingPlansQuery, useGetPricingPlanDetailsQuery,
	useDeletePricingPlanV2Mutation, useGetStationPricingPlanDetailsQuery
} from "services/pricingService";
import settingIcon from "assets/setting.svg";
import EditModel from "./components/EditModel";
import EditModelPlan from './components/EditModelPlan';
import { CreateModal, EditModal } from "./components/PlanModal/PlanModal";
import LookModel from "./components/EditModelPlan"
import "./Pricing.less";
import settingSvg from 'assets/setting.svg';

type Props = {
	shouldRefresh: boolean,
};
export type Users = Array<UserItem>;

const Pricing: React.FC<Props> = ({ shouldRefresh }): React.ReactElement => {
	const getFilterBy = (): string | undefined =>
		filter && [i18n.t("chargers.station"), i18n.t("chargers.type"), i18n.t("chargers.status")].includes(filter) ? filter : undefined;

	const getFilterQueryString = (
		key: string | null,
		filterBy: string | undefined,
		filterValue: string | null
	) =>
		filterBy && filterValue
			? key?.toLowerCase() + "_is_" + filterValue
			: undefined;

	const getKey = (filterBy?: string): string | null =>
		filterBy
			? (filterBy === "Station" ? "stationId" : filterBy.toLowerCase())
			: null;

	const getSortBy = (sort: string | null): string | undefined => sort ? sort : undefined;

	const { isLoggedIn } = useSelector(
		(state: rootReducerType) => state.authenticationReducer
	);

	const [limit] = useState(10);
	const [offset, setOffset] = useState<number>(0);
	const [previousOffset, setPreviousOffset] = useState<number>(offset);

	const [users, setUsers] = useState<Users>([]);

	const statusData = [
		i18n.t("chargers.available"),
		i18n.t("chargers.inUse"),
		i18n.t("chargers.unavailable"),
		i18n.t("chargers.offline"),
		i18n.t("chargers.faulted")
	];
	const typeData = [i18n.t("chargers.level2"), i18n.t("chargers.dcfc")];
	const stations = useGetStationsQuery({
		userName: emailToUsername(decodeJWT(isLoggedIn).email)
	}).data;
	const stationData = stations ? Object.keys(stations) : [];

	const dispatch = useDispatch();

	const [filter, setFilter] = useState<string | null>(null);
	const [filterValue, setFilterValue] = useState<string | null>(null);
	const [sort, setSort] = useState<string | null>(null);
	const [reset, setReset] = useState(false);
	const [currentPlanName, setCurrentPlanName] = useState<string>('')

	const { data, refetch } = useGetStationPricingPlansQuery({
		userName: emailToUsername(decodeJWT(isLoggedIn).email),
		limit: String(limit),
		offset: String(offset < 0 ? 0 : offset),
		sortBy: getSortBy(sort),
		filterBy: getFilterQueryString(getKey(getFilterBy()), getFilterBy(), filterValue),
	});

	const { data: pricingPlans, refetch: refetchPricingPlans } = useGetPricingPlansQuery(
		{
			userName: emailToUsername(decodeJWT(isLoggedIn).email),
		}
	);
	const { data: pricingPlanDetails, refetch: refetchPricingPlanDetails } = useGetPricingPlanDetailsQuery({
		pricingPlanName: currentPlanName
	}, { skip: !currentPlanName ? true : false });


	useEffect(() => {
		if (offset < previousOffset) return;
		if (offset === previousOffset) {
			const tempOffset = offset < 0 ? 0 : offset;
			dispatch(
				pricingApi.endpoints.getStationPricingPlans.initiate(
					{
						userName: emailToUsername(decodeJWT(isLoggedIn).email),
						limit: String(limit),
						offset: String(tempOffset),
						sortBy: getSortBy(sort),
						filterBy: getFilterQueryString(getKey(getFilterBy()), getFilterBy(), filterValue),
					},
					{ subscribe: false, forceRefetch: true }
				)
			);
		}
		setReset(false);
	}, [offset]);

	useEffect(() => {
		const tempOffset = offset <= 0 ? offset - 1 : 0;
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);
	}, [filterValue, sort]);

	useEffect(() => {
		if (!data || reset) return;
		const tempOffset = offset < 0 ? 0 : offset;
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);
		const counterCommonItems = counterCommonItemsArrays(data, users);
		const newItems = JSON.parse(JSON.stringify(
			offset <= 0
				? [...data]
				: (counterCommonItems === 0
					? [...users, ...data]
					: [...users]
				)));
		setUsers(newItems);
		setPreviousOffset(offset);
	}, [JSON.stringify(data), offset, reset]);

	useEffect(() => {
		if (reset) {
			setReset(false);
			refetch();
			refetchPricingPlans();
			refetchPricingPlanDetails();
		}
	}, [refetch, reset]);

	useEffect(() => {
		refetch();
		refetchPricingPlans();
		refetchPricingPlanDetails();
	}, [shouldRefresh]);

	const selectItems = [{ option: "Station" }, { option: "Type" }, { option: "Status" }];

	const handleReset = () => {
		const tempOffset = offset <= 0 ? offset - 1 : 0;
		setReset(true);
		setFilter(null);
		setFilterValue(null);
		setSort(null);
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);
	};

	const { width } = useWindowDimensions();

	const [deletePricingPlan, { isSuccess: deletePricingPlanSuccess, data: deletePricingPlanData }] = useDeletePricingPlanV2Mutation();

	useEffect(() => {
		if (deletePricingPlanSuccess) {
			refetchPricingPlans();
			Notification({
				type: deletePricingPlanData?.success ? "success" : "warning",
				message: deletePricingPlanData?.success ? "delete pricing plan success" : "delete pricing plan failed",
				description: "",
			});
			handleCancel();
		}

	}, [deletePricingPlanSuccess]);

	const deleteSelectPlan = () => {
		setIsModalOpenDelete(true);
	}

	const [pricingPlanToDelete, setPricingPlanToDelete] = useState<any[]>([]);


	const editPicingPlan = () => {
		setOpenEdit(true);
	}

	const createPricingPlan = () => {
		// setOpen(true);
		SetModal({ type: 'create' })
	}

	const [open, setOpen] = useState<boolean>(false);

	const [loading, setLoading] = useState<boolean>(false);

	const handleOk = () => {
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
			setOpen(false);
		}, 3000);
	};

	const handleCancel = () => {
		setOpen(false);
	};

	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const handelOkEdit = () => {
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
			setOpenEdit(false);
		}, 3000);
	}

	const handleCancelEdit = () => {
		setOpenEdit(false);
	}

	const onChange = (checkedValues: any[]) => {
		setPricingPlanToDelete(checkedValues);
	};

	//   const { data: pricingPlanDetails, refetch: refetchPricingPlanDetails } = useGetPricingPlanDetailsQuery({
	// 	pricingPlanName: currentPlanName
	// }, { skip: !currentPlanName ? true : false });

	const handlePlansSet = (e, item) => {
		e.preventDefault();
		setCurrentPlanName(item)
		SetModal({ type: 'create', name: item })
		dispatch(
			pricingApi.endpoints.getPricingPlanDetails.initiate(
				{
					pricingPlanName: item
				},
				{ subscribe: false, forceRefetch: true }
			)
		);
		editModelOk()
	}

	const [isModalOpenDelete, setIsModalOpenDelete] = useState<boolean>(false);

	const handleOkDelete = () => {
		deletePricingPlan({
			pricingPlanToDelete: pricingPlanToDelete
		})
		setIsModalOpenDelete(false);
	}

	const handleCancelDelete = () => {
		setIsModalOpenDelete(false);
	}

	const [editModel, setEditModel] = useState<boolean>(false)

	const editModelOk = () => {
		setEditModel(true)
	}

	const editModelCancel = () => {
		setEditModel(false)
	}

	const [editModelData, setEditModelData] = useState<Object>({});

	const [modal, SetModal] = useState<{
		type: string,
		name?: string
	} | null>(null)

	const [openPlan, setOpenPlan] = useState<boolean>(false);
	const handleOpenPlan = () => {
		setOpenPlan(true);
	}
	const handleCancelPlan = () => {
		setOpenPlan(false);
	}

	const handleOkPlan = () => {
		setOpenPlan(false);
	}

	const [pricingRecord, setPricingRecord] = useState<any>({});

	useEffect(() => {
		if (pricingRecord) {
			refetchStationPricingPlanDetails()
		}
	}, [pricingRecord])

	const { data: stationPricingPlanDetails, refetch: refetchStationPricingPlanDetails } = useGetStationPricingPlanDetailsQuery({
		pricingPlanName: pricingRecord.type == "DCFC" ? pricingRecord.priorityPricingPlan : pricingRecord.regularPricingPlan,
		station: pricingRecord.station
	}, { skip: pricingRecord.type ? false : true });

	const settingTap = () => {
		// setEditModel(true)
		let newItems:any = [...users];
		newItems = newItems.map((item:any, index) => {
			item.isFlag = !item.isFlag;
			return item;
		})
		setUsers(newItems);
	}

	return (
		width > 768 ? (<Container
			backButton={false}
			loadingPageName={"pricing"}
			subTitle={"pricing"}
			// renderCondition={true}
			renderCondition={data ? true : false}
		>
			<div className="pricing">
				<div className="pricing-slide">
					<div className="pricing-slide-container">
						<div className="pricing-slide-title">Current plans</div>
						<div className="pricing-slide-content">
							<Checkbox.Group style={{ width: '100%' }} onChange={onChange}>
								{
									pricingPlans && pricingPlans.map((item, index) => (
										<Checkbox key={index} value={item} style={{ width: '100%' }}>
											<div className="pricing-slide-content-item">
												<div className="pricing-slide-content-item-title">{item}</div>
												<img className="pricing-slide-content-item-img" src={settingIcon} alt=""
													onClick={(e) => handlePlansSet(e, item)} />
											</div>
										</Checkbox>
									))
								}
							</Checkbox.Group>
						</div>
					</div>
					<div style={{ padding: '10px 20px' }} className='pricing-slide-last-btn'>
						<Button type="primary" danger size='large'
							style={{
								margin: "10px 0", height: '40px', background: '#FD6B7E',
								width: '48%'
							}}
							onClick={() => createPricingPlan()}>Create Pricing Plan</Button>
						<Button type="primary" danger size='large'
							disabled={pricingPlanToDelete && pricingPlanToDelete.length > 0 ? false : true}
							style={{
								background: '#FF9F39', border: '1px solid #FF9F39', height: '40px',
								width: '48%'
							}} onClick={() => deleteSelectPlan()}>Delete Select Plan</Button>
					</div>
				</div>
				<div className="pricing-table">
				<div style={{display:'flex', justifyContent:'flex-end', margin: '10px'}}>
				<img src={settingSvg} onClick={() => settingTap()}></img>
				</div>
					<div style={{ width: '100%', boxSizing: 'border-box' }}>
						<PricingTable
							limit={limit}
							offset={offset < 0 ? 0 : offset}
							setOffset={setOffset}
							data={getDataToDisplay(users, limit, offset) as any[]}
							setNewSort={(value) => setSort(value)}
							shouldReset={(value) => setReset(value)}
							reset={reset}
							length={users.length + (users.length < limit ? 0 : 1)}
							setEditModel={setOpenEdit}
							setEditModelData={setEditModelData}
							users={users}
							setUsers={setUsers}
							handleOpenPlan={handleOpenPlan}
							setPricingRecord={setPricingRecord}
						/>
					</div>
				</div>
			</div>
			{
				modal &&
				(modal.name ?
					<EditModal
						planName={modal.name}
						refetch={refetchPricingPlans}
						onCancel={() => SetModal(null)}
					/> :
					<CreateModal
						refetch={refetchPricingPlans}
						onCancel={() => SetModal(null)}
					/>)
			}
			<LookModel open={openPlan} handleOk={handleOkPlan} handleCancel={handleCancelPlan} handleData={stationPricingPlanDetails}/>
			<EditModel
				open={openEdit} handleOk={handelOkEdit} handleCancel={handleCancelEdit}
				editModelData={editModelData} pricingPlans={pricingPlans}
			/>
			<Modal title="Delete Tips" visible={isModalOpenDelete} onOk={handleOkDelete} onCancel={handleCancelDelete}>
				<p>Are you sure you want to delete this item</p>
			</Modal>
		</Container>) : (<Container
			backButton={false}
			loadingPageName={i18n.t("users.pageName")}
			subTitle={i18n.t("users.subtitle")}
			// renderCondition={true}
			renderCondition={data ? true : false}
		>
			<div className="pricing">
				<div className="pricing-slide">
					<div className="pricing-slide-container">
						<div className="pricing-slide-title">Current plans</div>
						<div className="pricing-slide-content">
							<Checkbox.Group style={{ width: '100%' }} onChange={onChange}>
								{
									pricingPlans && pricingPlans.map((item, index) => (
										<Checkbox key={index} value={item} style={{ width: '100%' }}>
											<div className="pricing-slide-content-item">
												<div className="pricing-slide-content-item-title">{item}</div>
												<img className="pricing-slide-content-item-img" src={settingIcon} alt=""
													onClick={(e) => handlePlansSet(e, item)} />
											</div>
										</Checkbox>
									))
								}
							</Checkbox.Group>
						</div>
					</div>
					<div style={{ padding: '10px 20px' }} className='pricing-slide-last-btn'>
						<Button type="primary" danger size='large'
							style={{ margin: "10px 0", height: '50px', background: '#FD6B7E' }}
							onClick={() => createPricingPlan()}>Create Pricing Plan</Button>
						<Button type="primary" danger size='large'
							style={{
								background: '#FF9F39', border: '1px solid #FF9F39', height: '50px',
							}} onClick={() => deleteSelectPlan()}>Delete Select Plan</Button>
					</div>
				</div>
				<div style={{display:'flex', justifyContent:'flex-end', margin: '10px 0 0 0', cursor: 'pointer'}}>
					<img style={{cursor: 'pointer'}} src={settingSvg} onClick={() => settingTap()}></img>
				</div>
				<div className="pricing-table">
					<PricingTable
						limit={limit}
						offset={offset < 0 ? 0 : offset}
						setOffset={setOffset}
						data={getDataToDisplay(users, limit, offset) as any[]}
						setNewSort={(value) => setSort(value)}
						shouldReset={(value) => setReset(value)}
						reset={reset}
						length={users.length + (users.length < limit ? 0 : 1)}
						setEditModel={setOpenEdit}
						setEditModelData={setEditModelData}
						users={users}
							setUsers={setUsers}
							handleOpenPlan={handleOpenPlan}
							setPricingRecord={setPricingRecord}
					/>
				</div>
			</div>
			{/* <CreateModel
				open={open} handleOk={handleOk} handleCancel={handleCancel}
			/> */}
			<EditModel
				open={openEdit} handleOk={handelOkEdit} handleCancel={handleCancelEdit}
				editModelData={editModelData} pricingPlans={pricingPlans}
			/>
			<Modal title="Delete Tips" visible={isModalOpenDelete} onOk={handleOkDelete} onCancel={handleCancelDelete}>
				<p>Are you sure you want to delete this item</p>
			</Modal>
			{
				modal &&
				(modal.name ?
					<EditModal
						planName={modal.name}
						refetch={refetchPricingPlans}
						onCancel={() => SetModal(null)}
					/> :
					<CreateModal
						refetch={refetchPricingPlans}
						onCancel={() => SetModal(null)}
					/>)
			}
			{/* <EditModelPlan open={editModel} handleOk={editModelOk} handleCancel={editModelCancel}
				handleData={pricingPlanDetails} /> */}
			<LookModel open={openPlan} handleOk={handleOkPlan} handleCancel={handleCancelPlan} handleData={stationPricingPlanDetails}/>
		</Container>)

	);
};

export default Pricing;
